import { JobState } from "@eai-toolkit/client";
import { CheckCircleOutline, ErrorOutline } from "@mui/icons-material";
import type { Theme } from "@mui/material";
import {
  Button,
  CircularProgress,
  Dialog,
  Divider,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";

import { getJobDetailUrl, getJobLogsUrl, JOBS_ROUTE } from "@helpers/constants";

import stateRenderer from "../../../data/fieldRenderers/JobState";
import { useGetJobByIdQuery } from "../../../store/api/jobsApi";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  pollNewJobStatus,
  reviseNewJobSpecAfterFailure,
  selectNewJobForm,
} from "../../../store/slices/newJobFormSlice";
import { getStateBackgroundColor, jobStateDescription } from "../utils";

const DialogPaper = styled("div")(() => ({
  width: 500,
}));

const Wrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  margin: `${theme.spacing(10)} ${theme.spacing(4)}`,
}));

const Title = styled("div")(({ theme }) => ({
  margin: theme.spacing(2),
  display: "flex",
  alignItems: "center",
}));

const TitleText = styled("span")(({ theme }) => ({
  marginLeft: theme.spacing(1.5),
}));

const State = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
}));

const StateLabel = styled(Typography)(({ theme }) => ({
  marginRight: theme.spacing(1),
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  marginTop: theme.spacing(2.5),
  marginBottom: theme.spacing(2.5),
  width: "100%",
  opacity: 0.5,
}));

const iconStyle = (theme: Theme) => ({
  fontSize: theme.spacing(12),
});

const mainButtonStyle = (theme: Theme) => ({
  marginTop: theme.spacing(5),
});

const actionButtonStyle = (theme: Theme) => ({
  marginTop: theme.spacing(1),
});

function NewJobSubmissionDialog() {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const newJob = useAppSelector(selectNewJobForm);

  // we should only ever render this component if the job responseId is defined
  const jobId = newJob.responseId as NonNullable<typeof newJob.responseId>;
  const jobLogsUrl = getJobLogsUrl(jobId);

  const { data: jobQueryResult } = useGetJobByIdQuery(
    { jobId },
    {
      skip: !jobId,
      pollingInterval: newJob.state === "STATUS_POLLING" ? 2000 : undefined,
    },
  );
  const jobResponse = jobQueryResult?.data;

  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (newJob.state === "STATUS_POLLING") {
      // Cancel any existing polling and start new
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      dispatch(pollNewJobStatus({ signal: abortController.signal }));

      return () => {
        abortController.abort();
      };
    }
  }, [newJob.state, dispatch]);

  const jobResponseState = jobResponse?.state;
  const title = useMemo(() => {
    if (newJob.state === "ERROR_AFTER_SUBMIT") {
      return "Job failed to start successfully";
    }
    if (newJob.state === "SUCCESS" && jobResponseState === JobState.RUNNING) {
      return "Job started successfully";
    }
    if (!jobResponseState) {
      return "Fetching job information...";
    }
    return jobStateDescription(jobResponseState);
  }, [newJob.state, jobResponseState]);
  const runningQueueStates: JobState[] = [JobState.QUEUED, JobState.QUEUING];

  return (
    <Dialog open>
      <DialogPaper>
        <Wrapper>
          {newJob.state === "SUCCESS" ? (
            <CheckCircleOutline
              sx={iconStyle(theme)}
              htmlColor={getStateBackgroundColor(
                JobState.SUCCEEDED,
                theme.palette,
              )}
            />
          ) : newJob.state === "ERROR_AFTER_SUBMIT" ? (
            <ErrorOutline
              sx={iconStyle(theme)}
              htmlColor={getStateBackgroundColor(
                JobState.FAILED,
                theme.palette,
              )}
            />
          ) : (
            <CircularProgress size={theme.spacing(12)} />
          )}

          <Title>
            <TitleText>{title}</TitleText>
          </Title>

          {jobResponse && (
            <>
              <State>
                <StateLabel>Current state:</StateLabel>
                {stateRenderer(jobResponse.state).node}
              </State>
              {(Boolean(jobResponse.stateInfo) ||
                runningQueueStates.includes(jobResponse.state)) && (
                <>
                  <StyledDivider />
                  <Typography variant="body2" color="textSecondary">
                    {jobResponse.stateInfo || "Waiting for more info..."}
                  </Typography>
                  <StyledDivider />
                </>
              )}
            </>
          )}
          {newJob.state === "ERROR_AFTER_SUBMIT" ? (
            <>
              <Button
                color="primary"
                variant="contained"
                component={Link}
                to={jobLogsUrl}
                sx={mainButtonStyle(theme)}
              >
                View logs
              </Button>
              <Button
                color="primary"
                onClick={() => dispatch(reviseNewJobSpecAfterFailure())}
                size="small"
                sx={actionButtonStyle(theme)}
              >
                Edit configuration
              </Button>
            </>
          ) : (
            <>
              <Button
                color="primary"
                variant="contained"
                component={Link}
                to={getJobDetailUrl(jobId)}
                sx={mainButtonStyle(theme)}
              >
                View job details
              </Button>
              <Button
                color="primary"
                component={Link}
                to={JOBS_ROUTE}
                size="small"
                sx={actionButtonStyle(theme)}
              >
                View all jobs
              </Button>
            </>
          )}
        </Wrapper>
      </DialogPaper>
    </Dialog>
  );
}

export default NewJobSubmissionDialog;
